/** Stable colour per project for the time report's dots and charts.
 *
 * Two rules pull against each other here:
 *
 *  1. A project keeps its colour when the period changes. Rows reorder and drop
 *     as the window moves, and "Caitlin is the green one" has to survive that —
 *     so the *preferred* slot comes from the project's id, never its position.
 *  2. Two projects on screen together must never share a hue. Eight slots and a
 *     per-id hash guarantees collisions, and worse than randomly: `hash % 8`
 *     reduces to a digit sum (33 ≡ 1 mod 8), so ids 19, 46 and 55 all want slot
 *     8. That is exactly the column of identical red dots the report shipped.
 *
 * `assignProjectColorSlots` honours (1) where it can and enforces (2): each
 * project takes its preferred slot, and one whose slot is already claimed probes
 * forward to the next free slot. Assignment walks ids in ascending order rather
 * than report order, so re-sorting rows by duration never repaints anything.
 *
 * Past eight visible projects the slots repeat. That is acceptable in the table,
 * where every dot sits beside its project name — the charts fold their tail into
 * a neutral "Other" rather than lean on a repeated hue.
 *
 * The eight slots are the validated categorical palette in index.css
 * (`--series-1..8`) — fixed order, not cycled through generated hues.
 */
export const SERIES_SLOT_COUNT = 8;

/** The palette custom property for a slot in 1..SERIES_SLOT_COUNT. */
export function seriesVar(slot: number): string {
  return `var(--series-${slot})`;
}

/** The slot a project would like, before collisions are resolved. */
export function projectColorSlot(projectId: number | string): number {
  return (hash(String(projectId)) % SERIES_SLOT_COUNT) + 1;
}

/** Colour for a project seen on its own — no set to deduplicate against. */
export function projectColorVar(projectId: number | string): string {
  return seriesVar(projectColorSlot(projectId));
}

/** Slot per project for a set shown together: distinct while ids <= 8 slots.
 * Keyed by `String(projectId)`, so look up with the same conversion. */
export function assignProjectColorSlots(
  projectIds: Array<number | string>,
): Map<string, number> {
  const ids = Array.from(new Set(projectIds.map(String))).sort(byIdAscending);
  const slots = new Map<string, number>();
  const taken = new Set<number>();

  for (const id of ids) {
    const preferred = projectColorSlot(id);
    let slot = preferred;
    // The size guard is what terminates the probe once every slot is spoken for.
    if (taken.size < SERIES_SLOT_COUNT) {
      for (let step = 1; taken.has(slot); step++) {
        slot = ((preferred - 1 + step) % SERIES_SLOT_COUNT) + 1;
      }
    }
    slots.set(id, slot);
    taken.add(slot);
  }
  return slots;
}

/** Numeric where both ids are numbers, lexicographic otherwise (legacy uids). */
function byIdAscending(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** djb2, unsigned. Stable across sessions — no Math.random, no index. */
function hash(value: string): number {
  let h = 5381;
  for (let i = 0; i < value.length; i++) {
    h = ((h << 5) + h + value.charCodeAt(i)) >>> 0;
  }
  return h;
}
